import Phaser from 'phaser';
import { MAIN_SPAWN_FROM_HOME } from '../mainLayout.js';
import { GAME_WIDTH, GAME_HEIGHT, SCENE } from '../../config.js';
import { visitState } from '../../state/visitState.js';
import { showToast } from '../../ui/Toast.js';
import { loadVisitView, setupVisitWorld, depthSortVisitAvatars } from './VisitSceneBase.js';
import { fitCharacter, centreForFeet } from '../../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from '../roomPerspective.mjs';
import { detectHomeDoor, HOME_FLOOR_POLYLINE, arrivalDoor } from '../HomeScene.js';
import { attachGardenWindow } from '../../ui/GardenWindow.js';
import { ensureLoaded, loadCtx, prioritiseFor } from '../../ui/sceneLoading.js';
import { needsFor } from '../../assetManifest.mjs';

// Map the owner HomeScene's door targets to their read-only visitor equivalents.
const ROOM_TO_VISIT = {
  [SCENE.KITCHEN]: SCENE.VISIT_KITCHEN,
  [SCENE.SALON]: SCENE.VISIT_SALON,
  [SCENE.SECOND_FLOOR]: SCENE.VISIT_SECOND_FLOOR,
};

// The host's main interior. Uses the SAME painted door zones + floor as the owner
// HomeScene (via detectHomeDoor / HOME_FLOOR_POLYLINE), so navigating a friend's house
// is identical to your own: walk to the salon / kitchen / second-floor / patio door,
// or the right-side exit door back to the yard.
export class VisitHomeScene extends Phaser.Scene {
  constructor() { super(SCENE.VISIT_HOME); }

  async create(data) {
    const view = await loadVisitView(this, data);
    if (!view) return;
    if (!this.sys.isActive()) return;   // scene was stopped while the view was loading
    if (view.house?.locked) {  // host's house is locked — same as the owner scene bouncing out
      // The toast rides along: shown here it died with this scene the same frame.
      this.scene.start(SCENE.VISIT_MAIN, { toast: 'Dom jest zamknięty {lock}' });
      return;
    }

    // NOT built by makeVisitScene → load the host's art here (see VisitSceneBase). After
    // the locked-house bounce above, so we never download a house we are about to leave.
    await ensureLoaded(this, needsFor(this.scene.key, loadCtx({ hostState: view })));
    if (!this.sys.isActive()) return;   // scene was stopped while the art was loading
    prioritiseFor(this.scene.key, { hostState: view });

    const lvl = view.house?.level ?? 1;
    const bgKey = (lvl > 1 && this.textures.exists(`bg_home_lvl${lvl}`)) ? `bg_home_lvl${lvl}` : 'bg_home';
    this.perspective = ROOM_PERSPECTIVE.home;   // like the owner's house: smaller towards the back wall
    setupVisitWorld(this, view, {
      roomScene: 'HomeScene',
      bgKey,
      areaKey: 'home',
      spawn: { x: GAME_WIDTH / 2, y: GAME_HEIGHT - 240 },
      floorPolyline: HOME_FLOOR_POLYLINE,
      spawnX: data?.spawnX,
      spawnY: data?.spawnY ?? (data?.spawnFeetY != null ? centreForFeet(this, data.spawnFeetY) : undefined),
    });

    // The host's farm, live, behind the patio-door glass.
    attachGardenWindow(this, { homeBgKey: bgKey, getData: () => view });

    this._triggered = false;
    // Cooldown so we don't re-fire while standing in a zone — seeded with the door we ARRIVED
    // in, if any. Coming back from the farm lands your feet on the patio door itself, and
    // an unseeded cooldown sent you straight back out to the farm, over and over.
    this._lastDoor = arrivalDoor(this.natalka);
  }

  update(_t, dMs) {
    // create() is async — the avatar/walker aren't built for the first frame(s). Bail
    // until they exist (calling detectHomeDoor with no natalka throws and freezes Phaser).
    if (!this.natalka) return;
    this.walker.update(dMs);
    fitCharacter(this);
    this.animals?.update(dMs);
    depthSortVisitAvatars(this);       // the visitor behind the host is drawn behind her (was a fixed depth 10)
    if (this._triggered || this.furnitureSeats?.isSeated) return;

    const hit = detectHomeDoor(this.natalka);
    if (!hit) { this._lastDoor = null; return; }

    if (hit.kind === 'exit') {
      if (this._lastDoor === 'exit') return;
      this._triggered = true;
      // Land at the stair foot in the yard, like exiting your own house (HomeScene → MainScene).
      this.scene.start(SCENE.VISIT_MAIN, { spawnX: MAIN_SPAWN_FROM_HOME.x, spawnFeetY: MAIN_SPAWN_FROM_HOME.feetY, spawnFacing: MAIN_SPAWN_FROM_HOME.facing });
      return;
    }
    if (hit.kind === 'garden') {
      if (this._lastDoor === 'garden') return;
      this._triggered = true;
      this.scene.start(SCENE.VISIT_FARM, { from: 'home' });
      return;
    }

    // Room door (kitchen / salon / second floor).
    const { door } = hit;
    if (this._lastDoor === door.roomId) return;
    const unlocked = visitState.view?.house?.rooms?.[door.roomId];
    if (!unlocked) {
      this._lastDoor = door.roomId;   // host hasn't opened this room — can't peek inside
      showToast(this, 'Ten pokój jest zamknięty {lock}');
      return;
    }
    const target = ROOM_TO_VISIT[door.toScene];
    if (!target) { this._lastDoor = door.roomId; return; }
    this._triggered = true;
    this.scene.start(target);
  }
}
